import { DayButtonProps, DayPicker } from "react-day-picker";
import { ko } from "date-fns/locale";
import { twMerge } from "tailwind-merge";
import { DeletedHoliday, Holiday } from "hooks/console/useConsoleHoliday";
import { DAY_STATE_STYLE, getDayState, toDateStr } from "./holidayUtils";

type HolidayCalendarProps = {
    month: Date;
    holidayMap: Map<string, Holiday>;
    deletedMap: Map<string, DeletedHoliday>;
    loading: boolean; // 월 데이터 로딩 중 — 빈 목록으로 "+"가 깜박이지 않게 숨김
    today: string;
    onDayClick: (dateStr: string) => void;
};

// 모닝택배 휴무일 월 캘린더 — react-day-picker v9 + 커스텀 DayButton 셀
// v9 는 mode/onDayClick 이 없으면 비대화형으로 보고 DayButton 을 렌더하지 않으므로 onDayClick 을 DayPicker 에 넘긴다
export default function HolidayCalendar({ month, holidayMap, deletedMap, loading, today, onDayClick }: HolidayCalendarProps) {
    const DayCell = ({ day, modifiers, className, ...buttonProps }: DayButtonProps) => {
        const dateStr = toDateStr(day.date);
        const holiday = holidayMap.get(dateStr);
        const deleted = holiday ? undefined : deletedMap.get(dateStr);
        const state = getDayState(dateStr, holiday);
        // 새로 등록할 수 있는 칸(오늘 이후 평일, 미등록) — "+" 표시
        const canAdd = !loading && !holiday && !deleted && state === "business" && dateStr >= today;
        const style = DAY_STATE_STYLE[state];
        const dow = day.date.getDay();
        const numberColor =
            state === "public" || state === "manual" ? style.text : dow === 0 ? "text-[#E11D48]" : dow === 6 ? "text-[#2563EB]" : "text-[#222222]";

        return (
            <button
                {...buttonProps}
                type="button"
                className={twMerge(
                    "group relative w-full h-full min-h-[96px] flex flex-col items-start gap-1 p-[10px] text-left rounded-[6px] border border-transparent hover:border-[#4F46E5] transition-colors",
                    style.cell,
                    dateStr < today && "opacity-60",
                    dateStr === today && "border-[#4F46E5] border-2",
                )}
                aria-label={`${dateStr}${holiday ? ` ${holiday.name}` : deleted ? ` 삭제된 공휴일 ${deleted.name}` : canAdd ? " 휴무일 등록" : ""}`}
            >
                <span className={twMerge("text-[16px] font-[600]", numberColor)}>{day.date.getDate()}</span>
                {canAdd && (
                    <span
                        aria-hidden
                        title="휴무일 등록"
                        className="absolute top-[8px] right-[8px] w-[22px] h-[22px] rounded-full flex items-center justify-center text-[16px] leading-none text-[#4F46E5] bg-[#EEF2FF] opacity-40 group-hover:opacity-100 transition-opacity"
                    >
                        +
                    </span>
                )}
                {holiday && (
                    <span className="flex flex-col gap-1 w-full">
                        {holiday.kind === "early" && (
                            <span className="self-start text-[12px] text-white bg-[#2563EB] rounded-[4px] px-[6px] py-[1px] whitespace-nowrap">
                                {holiday.end_time} 종료
                            </span>
                        )}
                        <span className={twMerge("text-[13px] leading-[1.3] break-keep line-clamp-2", style.text)}>{holiday.name}</span>
                    </span>
                )}
                {deleted && (
                    <span className="text-[12px] leading-[1.3] break-keep line-clamp-2 text-[#AAAAAA] line-through" title="삭제된 공휴일 — 눌러서 복원">
                        {deleted.name}
                    </span>
                )}
            </button>
        );
    };

    return (
        <DayPicker
            month={month}
            locale={ko}
            hideNavigation
            showOutsideDays={false}
            onDayClick={date => onDayClick(toDateStr(date))}
            components={{ DayButton: DayCell, MonthCaption: () => <></> }}
            classNames={{
                root: "w-full",
                months: "w-full",
                month: "w-full",
                month_grid: "w-full table-fixed border-separate border-spacing-[6px]",
                weekdays: "",
                weekday: "text-[15px] font-[500] text-[#666666] py-2 [&:first-child]:text-[#E11D48] [&:last-child]:text-[#2563EB]",
                week: "",
                day: "p-0 align-top h-[96px]",
                day_button: "",
            }}
        />
    );
}
